import { useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, Eye, EyeOff, FileKey2, FileText, FileUp, Lock, Pencil, Plus, Rows3, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input, Textarea } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/misc";
import { api, ApiError } from "@/lib/api";
import { errorText } from "@/lib/query/client";
import { qk } from "@/lib/query/keys";
import { relativeTime } from "@/lib/format";
import { isWritableEnvName, MAX_ENV_BYTES, previewEnv } from "@/lib/env-preview";
import { emptyRow, envFromRows, hiddenLineCount, pairRows, rowProblems, rowsFromEnv, type EnvRow } from "@/lib/env-editor";
import type { EnvGitState, ProjectEnvFile, User } from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * A project's `.env` files, in its sheet (admin only). The list shows key names;
 * a value reaches the browser only when the admin retrieves it in the editor
 * (recorded in activity), and the editor forgets it on close. The point is that a
 * secret reaches the project folder without being pasted into an agent's chat,
 * whose transcript is kept for ever.
 */

const SUGGESTED = [".env", ".env.local", ".env.development", ".env.production", ".env.test"];
const OTHER = "__other__";

const GIT_BADGE: Record<EnvGitState, { label: string; tint?: string; destructive?: boolean; hint: string } | null> = {
  tracked: {
    label: "Tracked by git",
    destructive: true,
    hint: "This file is committed. Every value in it goes into the repository's history — run `git rm --cached` on it and add it to .gitignore.",
  },
  "not-ignored": {
    label: "Not in .gitignore",
    tint: "var(--kind-review)",
    hint: "Git does not ignore this file, so the next `git add .` commits it. Add it to .gitignore.",
  },
  ignored: null,
  "no-repo": { label: "No git repo", hint: "The folder is not a git repository." },
  unknown: null,
};

export function ProjectEnvSection({ projectId, users }: { projectId: string; users: User[] }) {
  const envQuery = useQuery({ queryKey: qk.projectEnv(projectId), queryFn: () => api.projectEnv(projectId), staleTime: 0 });
  const [editing, setEditing] = useState<{ filename: string | null } | null>(null);
  const [deleting, setDeleting] = useState<ProjectEnvFile | null>(null);
  const files = envQuery.data?.files ?? [];
  const nameOf = (id: string | null) => (id ? users.find((user) => user.id === id)?.displayName ?? id : "someone");

  return (
    <section className="space-y-2">
      <div className="flex items-center gap-2">
        <p className="flex-1 text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">Environment</p>
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditing({ filename: null })}>
          <Plus /> Add .env
        </Button>
      </div>

      {envQuery.isPending ? (
        <Skeleton className="h-14 w-full" />
      ) : envQuery.isError ? (
        <p className="text-[12px] text-destructive">{errorText(envQuery.error, "Could not read the env files")}</p>
      ) : files.length === 0 ? (
        <p className="text-[12.5px] text-muted-foreground">
          No .env file yet. Add one here rather than pasting secrets into a chat — it is written straight into the folder
          (readable by its owner only), and its values are shown only when you retrieve them.
        </p>
      ) : (
        <ul className="space-y-2">
          {files.map((file) => {
            const git = GIT_BADGE[file.git];
            const open = (file.mode & 0o077) !== 0;
            return (
              <li
                key={file.filename}
                className={cn(
                  "space-y-1.5 rounded-md border px-2.5 py-2",
                  file.git === "tracked" ? "border-destructive/50 bg-destructive/8" : "border-border/70 bg-surface/40",
                )}
              >
                <div className="flex flex-wrap items-center gap-1.5">
                  <FileKey2 className="size-3.5 shrink-0 text-muted-foreground" />
                  <code className="font-mono text-[12.5px] font-medium">{file.filename}</code>
                  <span className="text-[11.5px] text-muted-foreground">
                    {file.keys.length} key{file.keys.length === 1 ? "" : "s"}
                  </span>
                  {git ? (
                    <Badge
                      variant={git.destructive ? "destructive" : git.tint ? undefined : "outline"}
                      tint={git.tint}
                      title={git.hint}
                    >
                      {git.destructive || git.tint ? <AlertTriangle /> : null}
                      {git.label}
                    </Badge>
                  ) : null}
                  {open ? (
                    <Badge tint="var(--kind-review)" title="Other users on the machine can read this file. Saving it here resets it to 0600.">
                      mode {file.mode.toString(8).padStart(3, "0")}
                    </Badge>
                  ) : null}
                </div>
                {file.git === "tracked" ? (
                  <p className="text-[12px] font-medium text-destructive">
                    Git tracks this file, so its values are in the repository and will be committed again on the next
                    change. Untrack it (<code className="font-mono">git rm --cached {file.filename}</code>), add it to
                    .gitignore, and rotate any secret already pushed.
                  </p>
                ) : null}
                {file.keys.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {file.keys.map((key) => (
                      <span key={key} className="rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] text-foreground/85">
                        {key}
                      </span>
                    ))}
                  </div>
                ) : null}
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <p className="min-w-0 flex-1 text-[11.5px] text-muted-foreground">
                    {file.lastChange?.action === "written"
                      ? `Updated ${relativeTime(file.lastChange.at)} by ${nameOf(file.lastChange.actorId)}`
                      : `Changed on disk ${relativeTime(file.modifiedAt)}`}
                  </p>
                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setEditing({ filename: file.filename })}>
                    <Pencil /> View / edit
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 text-xs text-muted-foreground hover:text-destructive"
                    aria-label={`Delete ${file.filename}`}
                    onClick={() => setDeleting(file)}
                  >
                    <Trash2 />
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <EnvFileDialog
        projectId={projectId}
        open={editing !== null}
        initial={editing}
        files={files}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          void envQuery.refetch();
        }}
      />
      <DeleteEnvDialog
        projectId={projectId}
        file={deleting}
        onClose={() => setDeleting(null)}
        onDeleted={() => {
          setDeleting(null);
          void envQuery.refetch();
        }}
      />
    </section>
  );
}

type EnvView = "pairs" | "text";

const MASK = "[-webkit-text-security:disc]";
const FIELD_PROPS = {
  autoComplete: "off",
  autoCorrect: "off",
  autoCapitalize: "off",
  spellCheck: false,
  "data-1p-ignore": true,
  "data-lpignore": "true",
} as const;

/**
 * One file in the editor, like a secret in AWS Secrets Manager: a new file starts
 * as an empty draft; an existing one opens with its key names and the values
 * withheld until **Retrieve values** (a recorded reveal), after which the whole file
 * is the draft and Save replaces it. Before retrieving, the draft is new keys only,
 * merged in. The draft is seen as rows or as the file's text (`lib/env-editor.ts`),
 * and every value stays masked until its eye — or Show values — is pressed.
 */
function EnvFileDialog({
  projectId,
  open,
  initial,
  files,
  onClose,
  onSaved,
}: {
  projectId: string;
  open: boolean;
  initial: { filename: string | null } | null;
  files: ProjectEnvFile[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [choice, setChoice] = useState(".env");
  const [custom, setCustom] = useState("");
  const [view, setView] = useState<EnvView>("pairs");
  const [rows, setRows] = useState<EnvRow[]>([]);
  const [text, setText] = useState("");
  const [retrieved, setRetrieved] = useState(false);
  const [retrieving, setRetrieving] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [shown, setShown] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const reset = () => {
    setView("pairs");
    setRows([emptyRow()]);
    setText("");
    setRetrieved(false);
    setShowAll(false);
    setShown(new Set());
    setError(null);
  };

  // Every opening starts clean: nothing typed or retrieved in a previous opening survives it.
  useEffect(() => {
    if (!open) return;
    const name = initial?.filename ?? (files.some((file) => file.filename === ".env") ? ".env.local" : ".env");
    setChoice(SUGGESTED.includes(name) || files.some((file) => file.filename === name) ? name : OTHER);
    setCustom(SUGGESTED.includes(name) ? "" : name);
    reset();
  }, [open]);

  const filename = choice === OTHER ? custom.trim() : choice;
  const existing = files.find((file) => file.filename === filename) ?? null;
  // Before a retrieve, an existing file's draft is new keys only, merged in.
  const mode: "replace" | "merge" = existing && !retrieved ? "merge" : "replace";
  const content = view === "pairs" ? envFromRows(rows) : text;
  const preview = useMemo(() => previewEnv(content), [content]);
  const bytes = useMemo(() => new TextEncoder().encode(content).length, [content]);
  const problems = useMemo(() => (view === "pairs" ? rowProblems(rows) : []), [view, rows]);
  const nameOk = isWritableEnvName(filename);
  const tooBig = bytes > MAX_ENV_BYTES;
  const known = new Set(existing?.keys ?? []);
  const options = [...new Set([...files.map((file) => file.filename), ...SUGGESTED])];
  const visibleRows = pairRows(rows);
  const hidden = view === "pairs" ? hiddenLineCount(rows) : 0;

  const switchView = (next: EnvView) => {
    if (next === view) return;
    if (next === "text") setText(envFromRows(rows));
    else {
      const parsed = rowsFromEnv(text);
      setRows(pairRows(parsed).length === 0 ? [...parsed, emptyRow()] : parsed);
    }
    setView(next);
  };

  const pickFile = (next: string) => {
    setChoice(next);
    reset();
  };

  const retrieve = async () => {
    if (!existing) return;
    setRetrieving(true);
    setError(null);
    try {
      const result = await api.revealProjectEnv(projectId, existing.filename);
      // Keys typed before retrieving are laid over the file, as the merge would have done.
      const typed = pairRows(view === "pairs" ? rows : rowsFromEnv(text)).filter((row) => row.key.trim() !== "");
      const loaded = rowsFromEnv(result.content);
      for (const row of typed) {
        const same = loaded.find((candidate) => candidate.kind === "pair" && candidate.key === row.key.trim());
        if (same) same.value = row.value;
        else loaded.push(row);
      }
      setRows(loaded.length === 0 ? [emptyRow()] : loaded);
      setText(envFromRows(loaded));
      setRetrieved(true);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Could not retrieve the values");
    } finally {
      setRetrieving(false);
    }
  };

  const readFile = async (file: File | undefined) => {
    if (!file) return;
    if (file.size > MAX_ENV_BYTES) {
      setError(`That file is ${Math.ceil(file.size / 1024)} KB; an env file here is at most ${MAX_ENV_BYTES / 1024} KB.`);
      return;
    }
    setError(null);
    const loaded = await file.text();
    setText(loaded);
    setRows(rowsFromEnv(loaded));
    setView("text");
    if (fileInput.current) fileInput.current.value = "";
  };

  const updateRow = (id: string, patch: Partial<EnvRow>) =>
    setRows((current) => current.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  const removeRow = (id: string) =>
    setRows((current) => {
      const next = current.filter((row) => row.id !== id);
      return pairRows(next).length === 0 ? [...next, emptyRow()] : next;
    });
  const addRow = () => setRows((current) => [...current, emptyRow()]);

  /** Pasting a whole env file into a key field fills rows instead of one long key. */
  const pasteIntoKey = (id: string, event: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = event.clipboardData.getData("text");
    if (!/[=]/.test(pasted) || !/\n/.test(pasted.trim())) return;
    const incoming = pairRows(rowsFromEnv(pasted)).map((row) => ({ ...row, raw: null, base: null }));
    if (incoming.length === 0) return;
    event.preventDefault();
    setRows((current) => {
      const at = current.findIndex((row) => row.id === id);
      const target = current[at];
      const replaceTarget = target && target.key === "" && target.value === "";
      return [...current.slice(0, replaceTarget ? at : at + 1), ...incoming, ...current.slice(at + 1)];
    });
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await api.writeProjectEnv(projectId, filename, { content, mode });
      const count = result.file.keys.length;
      toast.success(
        `${result.created ? "Created" : mode === "merge" ? "Updated" : "Saved"} ${result.file.filename} — ${count} key${count === 1 ? "" : "s"}` +
          (result.invalidLines.length > 0 ? ` (line ${result.invalidLines.join(", ")} not KEY=value)` : ""),
      );
      reset();
      onSaved();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Could not save the file");
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    reset();
    onClose();
  };

  const masked = (id: string) => !showAll && !shown.has(id);
  const toggleShown = (id: string) =>
    setShown((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const empty = content.trim() === "";
  const canSave =
    !busy && nameOk && !tooBig && problems.length === 0 && (mode === "merge" ? preview.keys.length > 0 : !empty);

  return (
    <Dialog open={open} onOpenChange={(next) => !next && close()}>
      <DialogContent className="max-w-3xl [&>*]:min-w-0" onOpenAutoFocus={(event) => event.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{existing ? filename : "Add an env file"}</DialogTitle>
          <DialogDescription>
            Written into the project folder, readable by its owner only (0600). Values are shown only when you retrieve
            them here — each retrieve is recorded in activity — and never to agents.
          </DialogDescription>
        </DialogHeader>

        {initial?.filename ? null : (
          <div className="space-y-1">
            <Label htmlFor="env-filename">File</Label>
            <Select value={choice} onValueChange={pickFile}>
              <SelectTrigger id="env-filename" className="font-mono">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {options.map((name) => (
                  <SelectItem key={name} value={name} className="font-mono">
                    {name}
                    {files.some((file) => file.filename === name) ? " · exists" : ""}
                  </SelectItem>
                ))}
                <SelectItem value={OTHER}>Other…</SelectItem>
              </SelectContent>
            </Select>
            {choice === OTHER ? (
              <Input
                value={custom}
                onChange={(event) => setCustom(event.target.value)}
                placeholder=".env.staging"
                className="font-mono"
                {...FIELD_PROPS}
              />
            ) : null}
            {filename && !nameOk ? (
              <p className="text-xs text-destructive">A name is .env or .env.&lt;name&gt; — lower-case, no folder, not a template.</p>
            ) : null}
          </div>
        )}

        {existing && !retrieved ? (
          <div className="space-y-2 rounded-md border border-border/70 bg-surface/40 p-2.5">
            <div className="flex flex-wrap items-center gap-2">
              <Lock className="size-3.5 shrink-0 text-muted-foreground" />
              <p className="min-w-0 flex-1 text-[12.5px] text-muted-foreground">
                {existing.keys.length} key{existing.keys.length === 1 ? "" : "s"} in the file. Values are hidden.
              </p>
              <Button size="sm" variant="outline" className="h-7 text-xs" disabled={retrieving} onClick={() => void retrieve()}>
                <Eye /> {retrieving ? "Retrieving…" : "Retrieve values"}
              </Button>
            </div>
            {existing.keys.length > 0 ? (
              <div className="flex max-h-24 flex-wrap gap-1 overflow-y-auto scrollbar-slim">
                {existing.keys.map((key) => (
                  <span key={key} className="rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] text-foreground/85">
                    {key}=••••
                  </span>
                ))}
              </div>
            ) : null}
            <p className="text-[11.5px] text-muted-foreground">
              Retrieve to view, edit or remove values. Or add new keys below without retrieving — they are merged in, and
              a key already in the file is overwritten.
            </p>
          </div>
        ) : null}

        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <div role="tablist" aria-label="Editor view" className="flex h-8 rounded-md border border-input p-0.5 text-xs">
              {(
                [
                  ["pairs", "Key/value", Rows3],
                  ["text", ".env file", FileText],
                ] as const
              ).map(([value, label, Icon]) => (
                <button
                  key={value}
                  type="button"
                  role="tab"
                  aria-selected={view === value}
                  onClick={() => switchView(value)}
                  className={cn(
                    "flex items-center gap-1.5 rounded px-2.5 font-medium transition-colors [&_svg]:size-3.5",
                    view === value ? "bg-primary/15 text-primary" : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  <Icon /> {label}
                </button>
              ))}
            </div>
            <span className="min-w-0 flex-1 truncate text-[11.5px] text-muted-foreground">
              {existing && !retrieved ? <span className="hidden sm:inline">New keys</span> : null}
            </span>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-7 text-xs"
              aria-label={showAll ? "Hide values" : "Show values"}
              onClick={() => setShowAll((value) => !value)}
            >
              {showAll ? <EyeOff /> : <Eye />} <span className="hidden sm:inline">{showAll ? "Hide values" : "Show values"}</span>
            </Button>
            <Button type="button" size="sm" variant="ghost" className="h-7 text-xs" aria-label="Load file" onClick={() => fileInput.current?.click()}>
              <FileUp /> <span className="hidden sm:inline">Load file</span>
            </Button>
            <input ref={fileInput} type="file" className="hidden" onChange={(event) => void readFile(event.target.files?.[0])} />
          </div>

          {view === "pairs" ? (
            <div className="space-y-1.5">
              <div className="hidden grid-cols-[minmax(0,2fr)_minmax(0,3fr)_4rem] gap-2 px-0.5 text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground sm:grid">
                <span>Key</span>
                <span>Value</span>
              </div>
              <div className="max-h-[32dvh] space-y-2 sm:max-h-[45dvh] overflow-y-auto scrollbar-slim pr-0.5 sm:space-y-1.5">
                {visibleRows.map((row, index) => {
                  const multiline = row.value.includes("\n");
                  const valueClass = cn("font-mono text-[12.5px]", masked(row.id) && row.value && MASK);
                  return (
                    <div
                      key={row.id}
                      className="grid grid-cols-[minmax(0,1fr)_auto] gap-1.5 rounded-md border border-border/60 p-1.5 sm:grid-cols-[minmax(0,2fr)_minmax(0,3fr)_4rem] sm:gap-2 sm:border-0 sm:p-0"
                    >
                      <Input
                        aria-label={`Key, row ${index + 1}`}
                        value={row.key}
                        onChange={(event) => updateRow(row.id, { key: event.target.value })}
                        onPaste={(event) => pasteIntoKey(row.id, event)}
                        placeholder="KEY"
                        className={cn(
                          "h-8 font-mono text-[12.5px]",
                          mode === "merge" && known.has(row.key.trim()) && "border-primary/50",
                        )}
                        {...FIELD_PROPS}
                      />
                      <div className="col-span-2 row-start-2 sm:col-span-1 sm:row-start-auto">
                        {multiline ? (
                          <Textarea
                            aria-label={`Value of ${row.key || `row ${index + 1}`}`}
                            value={row.value}
                            onChange={(event) => updateRow(row.id, { value: event.target.value })}
                            rows={Math.min(row.value.split("\n").length, 6)}
                            className={cn("min-h-8 py-1.5", valueClass)}
                            {...FIELD_PROPS}
                          />
                        ) : (
                          <Input
                            aria-label={`Value of ${row.key || `row ${index + 1}`}`}
                            value={row.value}
                            onChange={(event) => updateRow(row.id, { value: event.target.value })}
                            placeholder="value"
                            className={cn("h-8", valueClass)}
                            {...FIELD_PROPS}
                          />
                        )}
                      </div>
                      <div className="col-start-2 row-start-1 flex items-start sm:col-start-auto sm:row-start-auto">
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="size-8"
                          aria-label={masked(row.id) ? "Show value" : "Hide value"}
                          disabled={showAll}
                          onClick={() => toggleShown(row.id)}
                        >
                          {masked(row.id) ? <Eye /> : <EyeOff />}
                        </Button>
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="size-8 text-muted-foreground hover:text-destructive"
                          aria-label={`Remove ${row.key || `row ${index + 1}`}`}
                          onClick={() => removeRow(row.id)}
                        >
                          <X />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={addRow}>
                  <Plus /> Add row
                </Button>
                <p className="min-w-0 flex-1 text-[11.5px] text-muted-foreground">
                  Paste a whole .env into a key field to fill the rows.
                  {hidden > 0 ? ` ${hidden} comment or other line${hidden === 1 ? " is" : "s are"} kept as written — see the .env file view.` : ""}
                </p>
              </div>
            </div>
          ) : (
            <Textarea
              aria-label={`${filename} contents`}
              value={text}
              onChange={(event) => setText(event.target.value)}
              placeholder={"# one per line\nDATABASE_URL=postgres://…\nAPI_KEY=…"}
              rows={12}
              className={cn("max-h-[35dvh] sm:max-h-[50dvh] font-mono text-[12.5px] leading-relaxed", !showAll && text && MASK)}
              {...FIELD_PROPS}
            />
          )}
        </div>

        {!empty ? (
          <p className="text-[11.5px] text-muted-foreground">
            {preview.keys.length} key{preview.keys.length === 1 ? "" : "s"} · {(bytes / 1024).toFixed(1)} KB
            {mode === "merge"
              ? ` · ${preview.keys.filter((key) => known.has(key)).length} overwritten, ${preview.keys.filter((key) => !known.has(key)).length} new`
              : ""}
          </p>
        ) : null}
        {view === "text" && preview.invalidLines.length > 0 ? (
          <p className="flex items-start gap-1.5 text-[11.5px] text-[var(--kind-review)]">
            <AlertTriangle className="mt-0.5 size-3 shrink-0" />
            Line{preview.invalidLines.length > 1 ? "s" : ""} {preview.invalidLines.slice(0, 8).join(", ")}
            {preview.invalidLines.length > 8 ? "…" : ""} {preview.invalidLines.length > 1 ? "are" : "is"} not KEY=value — saved as
            written.
          </p>
        ) : null}
        {problems.length > 0 ? (
          <ul className="space-y-0.5 text-[11.5px] text-destructive">
            {problems.slice(0, 5).map((problem) => (
              <li key={problem}>{problem}</li>
            ))}
          </ul>
        ) : null}
        {tooBig ? <p className="text-[11.5px] text-destructive">Over {MAX_ENV_BYTES / 1024} KB.</p> : null}
        {existing && retrieved && empty ? (
          <p className="text-[11.5px] text-muted-foreground">The draft is empty — delete the file instead of saving it empty.</p>
        ) : null}
        {error ? <p className="break-words text-xs text-destructive">{error}</p> : null}

        <DialogFooter>
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button onClick={() => void save()} disabled={!canSave}>
            {busy ? "Saving…" : mode === "merge" ? "Add keys" : existing ? "Save changes" : "Save file"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteEnvDialog({
  projectId,
  file,
  onClose,
  onDeleted,
}: {
  projectId: string;
  file: ProjectEnvFile | null;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => setError(null), [file?.filename]);

  const confirm = async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      await api.deleteProjectEnv(projectId, file.filename);
      toast.success(`Deleted ${file.filename}`);
      onDeleted();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Could not delete that file");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={file !== null} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-md [&>*]:min-w-0">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="size-4 shrink-0 text-destructive" /> Delete {file?.filename}?
          </DialogTitle>
          <DialogDescription>
            The file is removed from the project folder with its {file?.keys.length ?? 0} key
            {file?.keys.length === 1 ? "" : "s"}. No copy is kept, so anything that reads it stops working until you add
            it again.
          </DialogDescription>
        </DialogHeader>
        {error ? <p className="break-words text-xs text-destructive">{error}</p> : null}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={() => void confirm()} disabled={busy}>
            <Trash2 /> {busy ? "Deleting…" : "Delete file"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
